import {
	isStorefrontEmailMessageKind,
	STOREFRONT_SAMPLE_CODE,
	type StorefrontEmailMessageKind,
	type StorefrontMessageVariableModel,
} from './storefront-messages';

/**
 * PD-17377 — the three storefront emails a storefront owns (welcome, password
 * reset and sign-in code): their merge variables, their generated MJML and
 * subject, and the data the sender fills them with.
 *
 * The generated templates reproduce the emails the login server built in code
 * before this change (apps/login platform-notification.service.ts,
 * getNewUserEmailHtml, getPasswordResetEmailHtml and getSignInCodeEmailHtml):
 * the same logo, title, wording, code, button, store badges and footer, in
 * MJML so they open in the email template builder. They are stored as
 * `email_template` rows that the brand points at, and rendered with Mustache
 * and then MJML, like every other email template.
 */

/** Marks an `email_template` row as one storefront's message, not a Settings template. */
export const STOREFRONT_EMAIL_TEMPLATE_OWNER_TYPE = 'brand';

/**
 * What `email_template.metadata` holds on a storefront email. `ownerType` is
 * what Settings, Email Templates uses to leave the row out of its list (see
 * EmailTemplateService.findAll), the same convention marketing campaigns use.
 * The subject lives here because `email_template` has no subject column.
 */
export interface StorefrontEmailTemplateMetadataModel {
	ownerType: typeof STOREFRONT_EMAIL_TEMPLATE_OWNER_TYPE;
	brandId: number;
	messageKind: StorefrontEmailMessageKind;
	/** The subject line, with `{{variables}}` filled like a text (no HTML escaping). */
	subject: string;
}

/** Whether an `email_template.metadata` value marks a storefront email. */
export function readStorefrontEmailTemplateMetadata(
	metadata: unknown,
): StorefrontEmailTemplateMetadataModel | null {
	if (metadata == null || typeof metadata !== 'object') {
		return null;
	}
	const record = metadata as Record<string, unknown>;
	if (
		record.ownerType !== STOREFRONT_EMAIL_TEMPLATE_OWNER_TYPE ||
		typeof record.brandId !== 'number' ||
		!isStorefrontEmailMessageKind(record.messageKind)
	) {
		return null;
	}
	return {
		ownerType: STOREFRONT_EMAIL_TEMPLATE_OWNER_TYPE,
		brandId: record.brandId,
		messageKind: record.messageKind,
		subject: typeof record.subject === 'string' ? record.subject : '',
	};
}

/**
 * The `email_template.name` of a storefront email. The column is unique, so
 * the brand id is part of it; the row never appears in Settings, so the name
 * is only ever seen by Workd staff reading the table.
 */
export function storefrontEmailTemplateName(
	brandId: number,
	kind: StorefrontEmailMessageKind,
	attempt = 1,
): string {
	const what =
		kind === 'welcome'
			? 'welcome'
			: kind === 'password_reset'
				? 'password reset'
				: 'sign-in code';
	const base = `Storefront ${what} email (brand ${brandId})`;
	return attempt <= 1 ? base : `${base} ${attempt}`;
}

/** The variables every storefront email can use. */
const COMMON_EMAIL_VARIABLES: ReadonlyArray<StorefrontMessageVariableModel> = [
	{
		name: 'brandName',
		label: 'Brand name',
		description: "The storefront's brand name, as customers see it.",
		sampleValue: 'Your Brand',
	},
	{
		name: 'firstName',
		label: 'First name',
		description:
			"The customer's first name. Blank when the account has none.",
		sampleValue: 'Jordan',
	},
	{
		name: 'lastName',
		label: 'Last name',
		description: "The customer's last name. Blank when the account has none.",
		sampleValue: 'Lee',
	},
	{
		name: 'logoUrl',
		label: 'Email logo',
		description: "The brand's email logo (its storefront logo when it has none).",
		sampleValue: '',
	},
	{
		name: 'buttonColor',
		label: 'Button color',
		description: "The brand's accent color, used for the button.",
		sampleValue: '#3570dd',
	},
	{
		name: 'supportPhone',
		label: 'Support phone',
		description: "The brand's customer support phone number, when it has one.",
		sampleValue: '(555) 010-0100',
	},
	{
		name: 'supportEmail',
		label: 'Support email',
		description: "The brand's customer support email address, when it has one.",
		sampleValue: 'support@example.com',
	},
	{
		name: 'hasAppLinks',
		label: 'Has an app',
		description:
			'True when the storefront has an app in either store. Use {{#hasAppLinks}} ... {{/hasAppLinks}} to show something only then.',
		sampleValue: '',
	},
	{
		name: 'appStoreUrl',
		label: 'App Store link',
		description:
			"The storefront app's App Store link, when it has one. Use {{#appStoreUrl}} ... {{/appStoreUrl}} to show the badge only then.",
		sampleValue: '',
	},
	{
		name: 'googlePlayUrl',
		label: 'Google Play link',
		description:
			"The storefront app's Google Play link, when it has one. Use {{#googlePlayUrl}} ... {{/googlePlayUrl}} to show the badge only then.",
		sampleValue: '',
	},
];

/** One storefront email: its variables, generated subject and generated body. */
export interface StorefrontEmailMessageDefinitionModel {
	kind: StorefrontEmailMessageKind;
	label: string;
	description: string;
	variables: ReadonlyArray<StorefrontMessageVariableModel>;
	/** The variable that holds the link the email exists to deliver. */
	linkVariable: string;
	/**
	 * Variables the email must keep: its link, and for the sign-in email its
	 * code. Without them the email cannot do its job, so it is refused on save,
	 * and an email that somehow arrives at sending without them goes out in the
	 * built-in wording instead.
	 */
	requiredVariables: ReadonlyArray<string>;
	defaultSubject: string;
	defaultTemplate: string;
}

/**
 * The official store badges, the same hosted artwork the built-in emails
 * used: PNG because Gmail and Outlook do not display SVG, 40px tall (Apple's
 * minimum), with width and height also set as attributes because Outlook
 * sizes images from those only.
 */
const APP_BADGES_MJML = `{{#hasAppLinks}}
				<mj-divider border-color="#e5e7eb" border-width="1px" padding="20px 30px 0 30px" />
				<mj-text padding="20px 30px 0 30px"><strong>Get the {{brandName}} app</strong></mj-text>
				<mj-text padding="4px 30px 10px 30px">{{#appStoreUrl}}<a href="{{appStoreUrl}}" style="display: inline-block; margin: 4px 6px; text-decoration: none;"><img src="https://cdn.titanworkflow.com/titanworkflow/assets/img/store-badges/app-store-badge.png" alt="Download on the App Store" width="120" height="40" style="display: block; border: 0; width: 120px; height: 40px;" /></a>{{/appStoreUrl}}{{#googlePlayUrl}}<a href="{{googlePlayUrl}}" style="display: inline-block; margin: 4px 6px; text-decoration: none;"><img src="https://cdn.titanworkflow.com/titanworkflow/assets/img/store-badges/google-play-badge.png" alt="Get it on Google Play" width="134" height="40" style="display: block; border: 0; width: 134px; height: 40px;" /></a>{{/googlePlayUrl}}</mj-text>
				{{/hasAppLinks}}`;

/**
 * The shared frame of the emails: a white 600px card on a light gray page,
 * the logo, the title, the message, the button and a gray footer, in the
 * colors and sizes of the built-in emails. The button carries its colors on
 * the element itself, which MJML does, because mail clients recolor link text
 * otherwise. `beforeButton` is MJML (one or more elements); `closing` is the
 * content of the text after the button.
 */
function frame(options: {
	title: string;
	heading: string;
	beforeButton: string;
	buttonLink: string;
	buttonLabel: string;
	closing: string;
	footer: string;
}): string {
	return `<mjml>
	<mj-head>
		<mj-title>${options.title}</mj-title>
		<mj-attributes>
			<mj-all font-family="HelveticaNeue, Helvetica, Arial, sans-serif" />
			<mj-text color="#1d2531" font-size="13px" line-height="1.5" align="center" padding="10px 30px" />
		</mj-attributes>
	</mj-head>
	<mj-body background-color="#f4f4f4" width="600px">
		<mj-section background-color="#ffffff" padding="20px 0 0 0">
			<mj-column>
				<mj-image src="{{logoUrl}}" alt="{{brandName}}" width="200px" align="center" padding="0 20px 20px 20px" />
			</mj-column>
		</mj-section>
		<mj-section background-color="#ffffff" padding="0 0 20px 0">
			<mj-column>
				<mj-text font-size="25px" font-weight="bold" padding="20px 30px">${options.heading}</mj-text>
				${options.beforeButton}
				<mj-button href="${options.buttonLink}" background-color="{{buttonColor}}" color="#ffffff" font-size="16px" font-weight="bold" border-radius="4px" inner-padding="12px 30px" padding="30px 30px">${options.buttonLabel}</mj-button>
				<mj-text>${options.closing}</mj-text>
				${APP_BADGES_MJML}
			</mj-column>
		</mj-section>
		<mj-section background-color="#f4f4f4" padding="20px 0">
			<mj-column>
				<mj-text padding="0 20px">${options.footer}</mj-text>
			</mj-column>
		</mj-section>
	</mj-body>
</mjml>
`;
}

export const STOREFRONT_EMAIL_MESSAGE_DEFINITIONS: ReadonlyArray<StorefrontEmailMessageDefinitionModel> =
	[
		{
			kind: 'welcome',
			label: 'Welcome email',
			description:
				'Sent to a new customer added with an email address. The button opens the page where they set their password, through the app page when the storefront has an app.',
			variables: [
				...COMMON_EMAIL_VARIABLES,
				{
					name: 'setPasswordLink',
					label: 'Set-password link',
					description:
						'The link to the page where the customer sets their password. It expires in 24 hours.',
					sampleValue: 'https://shop.example.com/set-password?token=preview',
				},
			],
			linkVariable: 'setPasswordLink',
			requiredVariables: ['setPasswordLink'],
			defaultSubject: 'Welcome to {{brandName}} - Set Your Password',
			defaultTemplate: frame({
				title: 'Welcome to {{brandName}}',
				heading: 'Welcome to {{brandName}}',
				beforeButton:
					'<mj-text>Welcome to {{brandName}}! Your account has been created successfully.<br /><br /><strong>Please click the button below to set your password and complete your account setup.</strong></mj-text>',
				buttonLink: '{{setPasswordLink}}',
				buttonLabel: 'Set Password',
				closing:
					"Once you've set your password, you can sign in with your email address and password. You can also sign in without a password at any time by asking for a one-time code by email.<br /><br />If you received this in error, you may disregard this message.<br /><br /><strong>Sincerely<br />{{brandName}} Support</strong>",
				footer: 'This request will expire in 24 hours.',
			}),
		},
		{
			kind: 'password_reset',
			label: 'Forgot-password email',
			description:
				'Sent when a customer asks to reset their password. The button opens the page where they choose a new one.',
			variables: [
				...COMMON_EMAIL_VARIABLES,
				{
					name: 'resetLink',
					label: 'Reset link',
					description:
						'The link to the page where the customer chooses a new password. It expires in 24 hours.',
					sampleValue: 'https://shop.example.com/reset-password?token=preview',
				},
			],
			linkVariable: 'resetLink',
			requiredVariables: ['resetLink'],
			defaultSubject: 'Password Reset Request - {{brandName}}',
			defaultTemplate: frame({
				title: 'Password Reset',
				heading: 'Forgot Password Reset Instructions',
				beforeButton:
					'<mj-text>You are receiving this email because you or someone else requested a password reset on your account.<br /><br /><strong>Please click the button below to reset your password</strong></mj-text>',
				buttonLink: '{{resetLink}}',
				buttonLabel: 'Reset Password',
				closing:
					'If you received this in error, you may disregard this message.<br /><br /><strong>Sincerely<br />{{brandName}} Support</strong>',
				footer: 'This request will expire in 24 hours.',
			}),
		},
		{
			kind: 'sign_in',
			label: 'Sign-in code email',
			description:
				'Sent when a customer asks for a sign-in code by email. They type the code, or press the button on the device they want to use.',
			variables: [
				...COMMON_EMAIL_VARIABLES,
				{
					name: 'code',
					label: 'Code',
					description:
						'The six-digit sign-in code. It works once and expires in 15 minutes.',
					sampleValue: STOREFRONT_SAMPLE_CODE,
				},
				{
					name: 'signInLink',
					label: 'Sign-in link',
					description:
						'The link that signs the customer straight in on the device where they open it. It works once and expires in 15 minutes.',
					sampleValue: 'https://shop.example.com/sign-in-link?token=preview',
				},
			],
			linkVariable: 'signInLink',
			requiredVariables: ['code', 'signInLink'],
			defaultSubject: 'Your {{brandName}} sign-in code: {{code}}',
			defaultTemplate: frame({
				title: 'Your {{brandName}} sign-in code',
				heading: 'Your sign-in code',
				beforeButton:
					'<mj-text>Enter this code on the sign-in screen:</mj-text>\n\t\t\t\t<mj-text font-size="34px" font-weight="bold" letter-spacing="8px" font-family="Menlo, Consolas, monospace" padding="24px 30px">{{code}}</mj-text>\n\t\t\t\t<mj-text>Or, if you are on the device you want to use, sign in with one tap:</mj-text>',
				buttonLink: '{{signInLink}}',
				buttonLabel: 'Sign In',
				closing:
					'If you did not try to sign in, you can ignore this email. Nobody can sign in without this code.<br /><br /><strong>Sincerely<br />{{brandName}} Support</strong>',
				footer: 'This code expires in 15 minutes and can be used once.',
			}),
		},
	];

/** The definition of one storefront email. */
export function storefrontEmailMessageDefinition(
	kind: StorefrontEmailMessageKind,
): StorefrontEmailMessageDefinitionModel {
	const found = STOREFRONT_EMAIL_MESSAGE_DEFINITIONS.find(
		(definition) => definition.kind === kind,
	);
	if (found == null) {
		throw new Error(`No storefront email definition for ${kind}`);
	}
	return found;
}

/**
 * The required variables an email template no longer carries, as `{{name}}`
 * or `{{{name}}}`, spaces allowed inside the braces. Empty when it has them all.
 */
export function storefrontEmailMissingVariables(
	kind: StorefrontEmailMessageKind,
	template: string,
): Array<string> {
	return storefrontEmailMessageDefinition(kind).requiredVariables.filter(
		(name) => !new RegExp(`\\{\\{\\{?\\s*${name}\\s*\\}?\\}\\}`).test(template),
	);
}

/** Workd blue, the built-in emails' button color when a brand has no accent. */
export const STOREFRONT_EMAIL_DEFAULT_BUTTON_COLOR = '#3570dd';

/** The Workd logo, the built-in emails' logo when a brand has none. */
export const STOREFRONT_EMAIL_DEFAULT_LOGO_URL =
	'https://cdn.titanworkflow.com/titanworkflow/assets/img/workd-logo-black.svg';

/** What a storefront email is filled with. */
export interface StorefrontEmailViewDataInputModel {
	kind: StorefrontEmailMessageKind;
	/** The set-password, reset or sign-in link, by the email's kind. */
	link: string;
	/** The sign-in code, for the sign-in email. */
	code?: string | null;
	brandName: string;
	logoUrl?: string | null;
	buttonColor?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	supportPhone?: string | null;
	supportEmail?: string | null;
	appStoreUrl?: string | null;
	googlePlayUrl?: string | null;
}

/**
 * The Mustache view a storefront email renders with. Built in one place so the
 * login server (which sends the email) and crm-api (whose Brand page previews
 * it) fill the same names the same way. Blank optional values are omitted
 * rather than sent as empty strings, so a `{{#appStoreUrl}}` section is
 * skipped exactly when there is no link.
 */
export function buildStorefrontEmailViewData(
	input: StorefrontEmailViewDataInputModel,
): Record<string, string | boolean> {
	const present = (value: string | null | undefined): string | null => {
		const trimmed = value?.trim() ?? '';
		return trimmed === '' ? null : trimmed;
	};
	const appStoreUrl = present(input.appStoreUrl);
	const googlePlayUrl = present(input.googlePlayUrl);

	const view: Record<string, string | boolean> = {
		brandName: input.brandName,
		firstName: present(input.firstName) ?? '',
		lastName: present(input.lastName) ?? '',
		logoUrl: present(input.logoUrl) ?? STOREFRONT_EMAIL_DEFAULT_LOGO_URL,
		buttonColor:
			present(input.buttonColor) ?? STOREFRONT_EMAIL_DEFAULT_BUTTON_COLOR,
		supportPhone: present(input.supportPhone) ?? '',
		supportEmail: present(input.supportEmail) ?? '',
		hasAppLinks: appStoreUrl != null || googlePlayUrl != null,
	};
	if (appStoreUrl != null) {
		view.appStoreUrl = appStoreUrl;
	}
	if (googlePlayUrl != null) {
		view.googlePlayUrl = googlePlayUrl;
	}
	const code = present(input.code);
	if (code != null) {
		view.code = code;
	}
	view[storefrontEmailMessageDefinition(input.kind).linkVariable] = input.link;
	return view;
}
